import React, { useEffect, useRef, useState, forwardRef, useImperativeHandle } from 'react';
import { useTheme } from '../../context/ThemeContext';

const TurnstileCaptcha = forwardRef(({ 
  siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY,
  onVerify,
  onExpire,
  onError,
  action,
  cData,
  theme: forcedTheme,
  size = 'normal',
  tabIndex = 0,
  responseField = true,
  responseFieldName = 'cf-turnstile-response',
  retry = 'auto',
  retryInterval = 8000,
  refreshExpired = 'auto',
  appearance = 'always',
  execution = 'render',
  className = ''
}, ref) => {
  const { isDark } = useTheme();
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState(null);

  // Script yükleme fonksiyonu
  const loadTurnstileScript = () => {
    return new Promise((resolve, reject) => {
      // Eğer script zaten yüklüyse
      if (window.turnstile) {
        resolve(window.turnstile);
        return;
      }

      // Script elementini kontrol et
      const existingScript = document.querySelector('script[src*="challenges.cloudflare.com"]');
      if (existingScript) {
        existingScript.addEventListener('load', () => resolve(window.turnstile));
        existingScript.addEventListener('error', reject);
        return;
      }

      // Yeni script elementi oluştur
      const script = document.createElement('script');
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
      script.async = true;
      script.defer = true;
      
      script.onload = () => {
        if (window.turnstile) {
          resolve(window.turnstile);
        } else {
          reject(new Error('Turnstile script yüklendi ancak API kullanılamıyor'));
        }
      };
      
      script.onerror = () => reject(new Error('Turnstile script yüklenemedi'));
      
      document.head.appendChild(script);
    });
  };

  // Widget render fonksiyonu
  const renderWidget = async () => {
    try {
      if (!containerRef.current || !window.turnstile) return;

      // Mevcut widget'ı temizle
      if (widgetIdRef.current !== null) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch (e) {
          console.warn('Widget temizlenirken hata:', e);
        }
        widgetIdRef.current = null;
      }

      // Tema ayarı
      const theme = forcedTheme || (isDark ? 'dark' : 'light');

      // Widget konfigürasyonu
      const config = {
        sitekey: siteKey,
        theme,
        size,
        tabindex: tabIndex,
        callback: (token) => {
          // console.log('Turnstile doğrulandı:', token);
          onVerify && onVerify(token);
        },
        'expired-callback': () => {
          console.warn('Turnstile süresi doldu');
          onExpire && onExpire();
        },
        'error-callback': (error) => {
          console.error('Turnstile hatası:', error);
          setError(error);
          onError && onError(error);
        },
        'response-field': responseField,
        'response-field-name': responseFieldName,
        retry,
        'retry-interval': retryInterval,
        'refresh-expired': refreshExpired,
        appearance,
        execution
      };

      // Action ve cData opsiyonel parametreler
      if (action) config.action = action;
      if (cData) config.cData = cData;

      // Widget'ı render et
      widgetIdRef.current = window.turnstile.render(containerRef.current, config);
      setError(null);
      
    } catch (err) {
      console.error('Widget render hatası:', err);
      setError(err.message);
      onError && onError(err.message);
    }
  };

  // Reset fonksiyonu
  const reset = () => {
    if (widgetIdRef.current !== null && window.turnstile) {
      try {
        window.turnstile.reset(widgetIdRef.current);
        setError(null);
      } catch (err) {
        console.error('Widget reset hatası:', err);
      }
    }
  };

  // Response token alma
  const getResponse = () => {
    if (widgetIdRef.current !== null && window.turnstile) {
      try {
        return window.turnstile.getResponse(widgetIdRef.current);
      } catch (err) {
        console.error('Response alma hatası:', err);
        return null;
      }
    }
    return null;
  };

  // Widget'ı dışarıdan kontrol etmek için ref expose et
  useImperativeHandle(ref, () => ({
    reset,
    getResponse,
    isLoaded,
    error
  }));

  // Script yükleme ve widget render
  useEffect(() => {
    let mounted = true;

    const initTurnstile = async () => {
      try {
        await loadTurnstileScript();
        if (mounted) {
          setIsLoaded(true);
          await renderWidget();
        }
      } catch (err) {
        if (mounted) {
          console.error('Turnstile başlatma hatası:', err);
          setError(err.message);
          onError && onError(err.message);
        }
      }
    };

    initTurnstile();

    return () => {
      mounted = false;
      // Cleanup widget
      if (widgetIdRef.current !== null && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch (e) {
          console.warn('Widget cleanup hatası:', e);
        }
      }
    };
  }, []);

  // Tema değişikliğinde widget'ı yeniden render et
  useEffect(() => {
    if (isLoaded && window.turnstile) {
      renderWidget();
    }
  }, [isDark, forcedTheme]);

  if (!siteKey) {
    return (
      <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
        <p className="text-red-600 dark:text-red-400 text-sm">
          ⚠️ Turnstile Site Key tanımlanmamış. Lütfen VITE_TURNSTILE_SITE_KEY environment variable'ını ayarlayın.
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
        <p className="text-red-600 dark:text-red-400 text-sm">
          ⚠️ CAPTCHA yüklenirken hata oluştu: {error}
        </p>
        <button
          onClick={() => {
            setError(null);
            renderWidget();
          }}
          className="mt-2 px-3 py-1 text-xs bg-red-100 dark:bg-red-800 text-red-600 dark:text-red-300 rounded hover:bg-red-200 dark:hover:bg-red-700 transition-colors"
        >
          Tekrar Dene
        </button>
      </div>
    );
  }

  return (
    <div className={`turnstile-container ${className}`}>
      <div
        ref={containerRef}
        className={`turnstile-widget ${!isLoaded ? 'opacity-50' : ''}`}
        style={{ minHeight: size === 'compact' ? '65px' : '65px' }}
      />
      {!isLoaded && (
        <div className="flex items-center justify-center py-4">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
          <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">
            CAPTCHA yükleniyor...
          </span>
        </div>
      )}
    </div>
  );
});

export default TurnstileCaptcha; 